import { locationRowMatchesJurisdictionKeys } from "./jurisdictionCountyMatch";
import { parseProfileJurisdiction } from "./jurisdictionKeys";
import type { CountyStatePair } from "./mapboxCountyStatePairs";

export type PipelineTapGateResolution =
  | { allowed: true; reason: "unrestricted" | "jurisdiction_match" }
  | { allowed: false; reason: "no_county_state" | "no_jurisdiction_match" | "no_profile_keys" };

function effectiveJurisdictionKeys(params: {
  registrationProfileData: unknown | null;
  jurisdictionCountyStateKeys: ReadonlySet<string> | null;
}):
  | { kind: "unrestricted" }
  | { kind: "no_keys" }
  | { kind: "restricted"; keys: ReadonlySet<string> } {
  const profile = parseProfileJurisdiction(params.registrationProfileData ?? null);

  if (profile.kind === "unrestricted") {
    return { kind: "unrestricted" };
  }

  const keysFromStorage =
    params.jurisdictionCountyStateKeys && params.jurisdictionCountyStateKeys.size > 0
      ? params.jurisdictionCountyStateKeys
      : null;
  const keysFromProfile = profile.kind === "restricted" && profile.keys.size > 0 ? profile.keys : null;
  const effectiveKeys = keysFromStorage ?? keysFromProfile;

  if (!effectiveKeys || effectiveKeys.size === 0) {
    return { kind: "no_keys" };
  }

  return { kind: "restricted", keys: effectiveKeys };
}

/**
 * Profile-only gate step. Returns a definitive allow/deny when reverse geocode is unnecessary
 * (unrestricted or no usable jurisdiction keys). Returns `null` when county/state is required.
 */
export type PipelineTapGateWithoutGeocode =
  | { allowed: true; reason: "unrestricted" }
  | { allowed: false; reason: "no_profile_keys" };

export function resolvePipelineMapTapWithoutGeocode(params: {
  registrationProfileData: unknown | null;
  jurisdictionCountyStateKeys: ReadonlySet<string> | null;
}): PipelineTapGateWithoutGeocode | null {
  const access = effectiveJurisdictionKeys(params);
  if (access.kind === "unrestricted") {
    return { allowed: true, reason: "unrestricted" };
  }
  if (access.kind === "no_keys") {
    return { allowed: false, reason: "no_profile_keys" };
  }
  return null;
}

/**
 * Whether pipeline vector details may be shown for a tap at `lng/lat`, using the same profile +
 * jurisdiction key sources as the rest of web (`parseProfileJurisdiction` + optional stored keys).
 *
 * Profile unrestricted / missing keys are decided before geoPairs (so empty pairs do not deny
 * unrestricted users). Callers that can skip Mapbox RTT should use
 * {@link resolvePipelineMapTapWithoutGeocode} first.
 */
export function resolvePipelineMapTapAllowed(params: {
  registrationProfileData: unknown | null;
  jurisdictionCountyStateKeys: ReadonlySet<string> | null;
  geoPairs: readonly CountyStatePair[];
}): PipelineTapGateResolution {
  const { geoPairs } = params;

  const access = effectiveJurisdictionKeys(params);
  if (access.kind === "unrestricted") {
    return { allowed: true, reason: "unrestricted" };
  }
  if (access.kind === "no_keys") {
    return { allowed: false, reason: "no_profile_keys" };
  }

  if (!geoPairs.length) return { allowed: false, reason: "no_county_state" };

  const hit = geoPairs.some((pair) =>
    locationRowMatchesJurisdictionKeys(pair.county, pair.state, access.keys),
  );

  return hit
    ? { allowed: true, reason: "jurisdiction_match" }
    : { allowed: false, reason: "no_jurisdiction_match" };
}
